import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import i18n from 'localization/i18nextConf';
import { I18nextProvider } from 'react-i18next';
import App from './App.tsx';
import 'bootstrap/dist/css/bootstrap.css';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { Replay } from '@sentry/replay';
import 'assets/scss/index.scss';
import { HelmetProvider } from 'react-helmet-async';

// Global unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
	// Log the error to Sentry
	Sentry.captureException(event.reason, {
		tags: { source: 'unhandled-promise-rejection' },
		extra: { promise: event.promise },
	});

	// Prevent the default browser behavior (console error)
	event.preventDefault();

	// Log to console for development
	console.warn('Unhandled promise rejection:', event.reason);
});

// Development-only: Quiet known noisy React warning about defaultProps on memo components (e.g., Gallery2)
if (import.meta.env.DEV) {
	const originalError = console.error;
	console.error = function (...args) {
		const firstArg = args[0];
		if (
			typeof firstArg === 'string' &&
			firstArg.includes('Support for defaultProps will be removed from memo components')
		) {
			return; // suppress specific warning
		}
		return originalError.apply(console, args);
	};
}

// Custom QueryClient with Sentry integration for API performance
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			onSuccess: (data, variables, context) => {
				// Optionally add a Sentry breadcrumb for successful queries
				Sentry.addBreadcrumb({
					category: 'react-query',
					message: `Query success: ${context?.queryKey || ''}`,
					level: 'info',
					data: { variables, data },
				});
			},
			onError: (error, variables, context) => {
				// Capture API errors as Sentry events
				Sentry.captureException(error, {
					tags: { source: 'react-query', queryKey: context?.queryKey || '' },
					extra: { variables },
				});
			},
			onSettled: (data, error, variables, context) => {
				// Optionally end a Sentry span/transaction here if you start one per query
			},
		},
	},
});

Sentry.init({
	dsn: import.meta.env.VITE_SENTRY_DSN,
	environment: import.meta.env.VITE_SENTRY_ENV,
	release: import.meta.env.VITE_SENTRY_RELEASE,
	// Do not send PII by default. Explicitly map known non-sensitive user fields if required.
	sendDefaultPii: false,
	integrations: [new BrowserTracing(), new Replay()],
	// Use environment-controlled sampling to avoid high volume in production by mistake
	tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) || 0.05,
	replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE) || 0.0,
	replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE) || 1.0,
	beforeSend(event) {
		// Remove sensitive data here if needed
		if (event.user) {
			delete event.user.email;
			delete (event.user as any).ip_address;
		}
		return event;
	},
});

ReactDOM.createRoot(document.getElementById('root')!).render(
	<HelmetProvider>
		<BrowserRouter
			basename={import.meta.env.VITE_APP_BASE_URL}
			future={{
				v7_startTransition: true,
				v7_relativeSplatPath: true,
			}}
		>
			<I18nextProvider i18n={i18n}>
				<QueryClientProvider client={queryClient}>
					<App />
				</QueryClientProvider>
			</I18nextProvider>
		</BrowserRouter>
	</HelmetProvider>,
);
